import { useState, useEffect } from 'react';
import { FormControl, Button, Glyphicon, FormGroup, ControlLabel, InputGroup, HelpBlock } from 'react-bootstrap';
import styled from 'styled-components';

const SSN_FIELD_WIDTH = 118;
const SSN_ICON_BUTTON_WIDTH = 34;

const InputGroupWrap = styled(InputGroup)`
    display: inline-flex;
    width: auto;
    max-width: 100%;
    .form-control {
        width: ${SSN_FIELD_WIDTH}px !important;
        min-width: ${SSN_FIELD_WIDTH}px !important;
        max-width: ${SSN_FIELD_WIDTH}px !important;
        flex: none !important;
        height: 34px;
        box-shadow: none !important;
    }
    .input-group-btn {
        display: inline-flex;
    }
    .input-group-btn > .btn + .btn {
        margin-left: 0;
    }
`;

const ErrorInputGroup = styled(InputGroupWrap)`
    .form-control, .input-group-addon {
        border-left: 3px solid #a94442;
        background-color: #fdf2f2;
    }
`;

const SsnDisplayAddon = styled(InputGroup.Addon)`
    width: ${SSN_FIELD_WIDTH}px !important;
    min-width: ${SSN_FIELD_WIDTH}px !important;
    height: 34px;
    line-height: 20px;
    padding: 6px 10px !important;
    background: #f8fafc !important;
    border-color: #cbd5e1 !important;
    border-radius: 6px 0 0 6px !important;
    color: #334155 !important;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
`;

const IconButton = styled(Button)<{ $isLast?: boolean; $active?: boolean }>`
    width: ${SSN_ICON_BUTTON_WIDTH}px;
    min-width: ${SSN_ICON_BUTTON_WIDTH}px;
    height: 34px;
    padding: 0 !important;
    border-color: #cbd5e1 !important;
    background: ${p => (p.$active ? '#eff6ff' : '#fff')} !important;
    color: ${p => (p.$active ? '#1d4ed8' : '#475569')} !important;
    border-radius: ${p => (p.$isLast ? '0 6px 6px 0' : '0')} !important;
    box-shadow: none !important;
    :hover,
    :focus {
        background: ${p => (p.$active ? '#dbeafe' : '#f8fafc')} !important;
        border-color: #94a3b8 !important;
        color: ${p => (p.$active ? '#1e40af' : '#1e293b')} !important;
    }
    :disabled {
        opacity: 0.45;
        background: #fff !important;
        color: #94a3b8 !important;
    }
`;

const digitsOnly = (value: string): string => (value || '').replace(/\D/g, '').substring(0, 9);

const formatSsn = (value: string): string => {
    const digits = digitsOnly(value);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return digits.substring(0, 3) + '-' + digits.substring(3);
    return digits.substring(0, 3) + '-' + digits.substring(3, 5) + '-' + digits.substring(5);
};

const maskSsnForDisplay = (value: string): string => {
    const digits = digitsOnly(value);
    if (!digits) return '';
    if (digits.length < 4) return '***-**-' + digits;
    return '***-**-' + digits.slice(-4);
};

interface IProps {
    value: string;
    onChange: (value: string) => void;
    filesTaxesInUs?: boolean | null;
    isDirty: boolean;
    isValid: boolean;
    errorMessage?: string;
    style?: React.CSSProperties;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
}

const SsnInput = (props: IProps) => {
    const [editMode, setEditMode] = useState(false);
    const [showFull, setShowFull] = useState(false);
    const [localValue, setLocalValue] = useState(digitsOnly(props.value || ''));

    useEffect(() => {
        setLocalValue(digitsOnly(props.value || ''));
    }, [props.value]);

    const handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl>) => {
        const value = digitsOnly(e.currentTarget.value);
        setLocalValue(value);
        props.onChange(value);
    };

    const finishEdit = () => {
        setEditMode(false);
        setShowFull(false);
    };

    const startEdit = () => {
        setEditMode(true);
        setShowFull(false);
    };

    const usesUsTaxIdentity = props.filesTaxesInUs !== false;
    const label = usesUsTaxIdentity ? 'SSN:' : 'TID:';
    const fieldName = usesUsTaxIdentity ? 'SSN' : 'TID';
    const displayValue = localValue
        ? (showFull ? formatSsn(localValue) : maskSsnForDisplay(localValue))
        : '';
    const editDisplayValue = formatSsn(localValue);
    const isEmpty = !props.value || digitsOnly(props.value).length === 0;
    const showError = !props.isValid && (props.isDirty || isEmpty);
    const InputGroupComponent = showError ? ErrorInputGroup : InputGroupWrap;
    const canReveal = digitsOnly(localValue).length === 9;
    const actionsDisabled = props.disabled || props.readOnly;

    return (
        <FormGroup style={{ marginRight: 10, ...props.style }} className={props.className} validationState={null}>
            <ControlLabel>{label}&nbsp;</ControlLabel>
            <InputGroupComponent>
                {!editMode ? (
                    <SsnDisplayAddon>
                        <div style={{ width: SSN_FIELD_WIDTH - 20 }}>
                            {displayValue || '—'}
                        </div>
                    </SsnDisplayAddon>
                ) : (
                    <FormControl
                        name='ssnDecrypted'
                        type='text'
                        inputMode='numeric'
                        autoComplete='off'
                        aria-label={fieldName}
                        placeholder={usesUsTaxIdentity ? '000-00-0000' : '9 digits'}
                        value={editDisplayValue}
                        maxLength={11}
                        onChange={handleInputChange}
                        disabled={props.disabled}
                        readOnly={props.readOnly}
                        autoFocus
                    />
                )}
                <InputGroup.Button>
                    {!editMode && (
                        <IconButton
                            type='button'
                            onClick={() => setShowFull(!showFull)}
                            disabled={actionsDisabled || !canReveal}
                            aria-label={showFull ? `Hide ${fieldName}` : `Show ${fieldName}`}
                            title={showFull ? `Hide ${fieldName}` : `Show ${fieldName}`}
                            $active={showFull}
                        >
                            <Glyphicon glyph={showFull ? 'eye-close' : 'eye-open'} />
                        </IconButton>
                    )}
                    <IconButton
                        type='button'
                        onClick={() => (editMode ? finishEdit() : startEdit())}
                        disabled={actionsDisabled}
                        aria-label={editMode ? `Done editing ${fieldName}` : `Edit ${fieldName}`}
                        title={editMode ? `Done editing ${fieldName}` : `Edit ${fieldName}`}
                        $isLast
                        $active={editMode}
                    >
                        <Glyphicon glyph={editMode ? 'ok' : 'pencil'} />
                    </IconButton>
                </InputGroup.Button>
            </InputGroupComponent>
            {showError && (
                <HelpBlock style={{ color: '#a94442', paddingLeft: 0 }}>
                    {props.errorMessage || `${fieldName} must be exactly 9 digits`}
                    {isEmpty && !editMode && ` — use the pencil icon to enter ${fieldName}`}
                </HelpBlock>
            )}
        </FormGroup>
    );
};

export default SsnInput;
